import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { API } from '@/App';
import { Mail, ChevronDown, ChevronRight } from 'lucide-react';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/**
 * What practitioners are writing to their clients.
 *
 * Their text goes live immediately — they're independent and shouldn't wait on approval to
 * say hello. This is the window the owner asked for: "Live immediately, but flag it in Admin
 * so I can read what's going out" (Jun 2026). Read-only by design.
 */
export default function AdminPractitionerEmails() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => {
    axios.get(`${API}/admin/practitioner-emails`, auth())
      .then(r => setData(r.data))
      .catch(() => setData({ practitioners: [], total: 0 }));
  }, []);

  if (!data) return null;

  return (
    <Card className="bg-slate-900/70 border-white/10" data-testid="admin-practitioner-emails">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-cyan-300" />
          <h3 className="text-white font-medium">What practitioners are writing to clients</h3>
          <span className="ml-auto text-xs text-gray-400" data-testid="practitioner-emails-count">
            {data.total} {data.total === 1 ? 'practitioner' : 'practitioners'}
          </span>
        </div>
        <p className="text-sm text-gray-400 mb-4 max-w-3xl">{data.note}</p>

        {data.total === 0 ? (
          <p className="text-sm text-gray-500" data-testid="practitioner-emails-empty">
            Nobody has personalized their client emails yet. Until they do, clients get the
            platform wording.
          </p>
        ) : (
          <div className="rounded-lg border border-white/10 bg-slate-950/40 divide-y divide-white/5">
            {data.practitioners.map(p => {
              const isOpen = open[p.practitioner_id];
              const stages = Object.keys(p.messages || {});
              return (
                <div key={p.practitioner_id} data-testid={`practitioner-email-row-${p.practitioner_id}`}>
                  <button
                    onClick={() => setOpen(o => ({ ...o, [p.practitioner_id]: !isOpen }))}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/[0.03] transition-colors duration-150"
                  >
                    {isOpen
                      ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                    <span className="text-sm text-white">
                      {p.business_name || p.display_name || p.booking_slug}
                    </span>
                    <span className="text-xs text-gray-500">
                      {stages.length > 0 && `${stages.length} custom message${stages.length === 1 ? '' : 's'}`}
                      {p.session_overrides.length > 0 &&
                        `${stages.length ? ' · ' : ''}${p.session_overrides.length} per-session`}
                      {p.presets.length > 0 && ` · ${p.presets.length} reminders`}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="px-10 pb-4 space-y-3">
                      {p.contact_email && (
                        <p className="text-xs text-gray-400">Replies go to {p.contact_email}</p>
                      )}
                      {stages.map(stage => (
                        <div key={stage}>
                          <p className="text-xs uppercase tracking-wide text-cyan-300/70 mb-1">{stage}</p>
                          <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">
                            {p.messages[stage]}
                          </p>
                        </div>
                      ))}
                      {p.session_overrides.map(o => (
                        <div key={o.session_type_id}>
                          <p className="text-xs uppercase tracking-wide text-violet-300/70 mb-1">
                            {o.session_name} (confirmation override)
                          </p>
                          <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed">{o.text}</p>
                        </div>
                      ))}
                      {p.presets.length > 0 && (
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                            Platform reminders they've ticked
                          </p>
                          <ul className="text-sm text-gray-300 list-disc pl-5 space-y-0.5">
                            {p.presets.map(pr => <li key={pr.id}>{pr.label}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
